import { cn } from "@/lib/utils";
import React from "react";

export interface ButtonLinkProps extends React.ComponentProps<"a"> {
  variant?:
  | "primary"
  | "secondary"
  | "danger"
  | "success"
  | "warning"
  | "outline"
  | "none"
  | "linkPrimary"
  | "outlinePrimary";
  isLoading?: boolean;
}

const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      children,
      variant = "primary",
      className = "w-full",
      isLoading,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "tracking-wide transition-colors duration-200 transform rounded-lg focus:outline-none focus:ring focus:ring-opacity-50 cursor-pointer inline-flex items-center justify-center";

    const variantStyles = {
      primary:
        "bg-brand-500 hover:bg-brand-400 focus:bg-brand-400 focus:ring-brand-300 text-white py-2 px-7",
      linkPrimary:
        "bg-transparent hover:bg-transparent focus:bg-transparent text-brand-500 underline hover:underline focus:underline py-2  text-left justify-start",
      secondary:
        "bg-gray-500 hover:bg-gray-500 focus:bg-gray-500 focus:ring-gray-300 text-white py-2 px-7",
      danger:
        "bg-red-500 hover:bg-red-400 focus:bg-red-400 focus:ring-red-300 text-white py-2 px-7",
      success:
        "bg-green-500 hover:bg-green-400 focus:bg-green-400 focus:ring-green-300 text-white py-2 px-7",
      warning:
        "bg-yellow-500 hover:bg-yellow-400 focus:bg-yellow-400 focus:ring-yellow-300 text-white py-2 px-7",
      outline:
        "border-2 border-brand-500 bg-transparent text-black hover:bg-brand-500 hover:text-white focus:bg-brand-500 focus:text-white focus:ring-brand-300 py-1.5 px-7",
      none: "border-2 border-transparent text-black hover:border-brand-500 hover:text-brand-500 focus:border-brand-500 focus:ring-brand-300 py-1.5 px-7",
      outlinePrimary:
        "border-2 bg-brand-50 border-brand-500 text-black hover:border-brand-500 text-brand-500 focus:border-brand-500 focus:ring-brand-300 py-1.5 px-1",
    };

    return (
      <a
        ref={ref}
        {...props}
        className={cn(
          `${baseStyles} ${variantStyles[variant]} ${className} ${isLoading ? "opacity-50 pointer-events-none" : ""
          }`
        )}
        target={props.target || "_blank"}
        rel="noopener noreferrer"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <svg
              className="mr-3 -ml-1 size-5 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading ...
          </span>
        ) : (
          children
        )}
      </a>
    );
  }
);

ButtonLink.displayName = "ButtonLink";

export { ButtonLink };
